import { FC, SyntheticEvent, useEffect, useState } from 'react';
import { Autocomplete, AutocompleteInputChangeReason, Chip, TextField, Typography } from '@mui/material';
import { SORT_DIR } from 'utils/enums';
import s from './index.module.scss';
import { PAGINATION_LIMIT } from 'utils/constants';
import { useDebounce, useModal } from 'utils/hooks';
import { ServicesService } from 'utils/services';
import { CustomModal, IOption } from 'components/elements';

interface ServiceAutocompleteProps {
	servicesValue: IOption[];
	onChange: (value: IOption[]) => void;
}

export const ServiceAutocomplete: FC<ServiceAutocompleteProps> = ({ servicesValue, onChange }) => {
	const [options, setOptions] = useState<IOption[]>([]);
	const [isLoading, setIsLoading] = useState(false);
	const [inputValue, setInputValue] = useState('');
	const debouncedInputValue = useDebounce(inputValue);

	useEffect(() => {
		const trimmedValue = debouncedInputValue.trim();
		if (trimmedValue) {
			handleGetOptions(trimmedValue);
		}
	}, [debouncedInputValue]);

	const handleGetOptions = async (value: string) => {
		try {
			setIsLoading(true);
			const { data } = await ServicesService.getServices({
				search: value,
				sorting: {
					field: 'name',
					direction: SORT_DIR.ASC,
				},
				pagination: {
					offset: 0,
					limit: PAGINATION_LIMIT.SMALL,
				},
			});
			setOptions(data.results.map((service) => ({ title: service.name, value: service.id })));
		} catch (error) {
			console.error('Error during getting services by name', error);
		} finally {
			setIsLoading(false);
		}
	};

	const handleChangeAutocompleteInput = (_: SyntheticEvent, value: string, reason: AutocompleteInputChangeReason) => {
		if (reason === 'input') {
			setInputValue(value);
		}
	};

	const handleChangeAutocomplete = (_: SyntheticEvent, value: IOption[]) => {
		onChange(value);
		setInputValue('');
	};

	const [isOpen, handleOpen, handleClose] = useModal();

	return (
		<Autocomplete
			multiple
			loading={isLoading}
			className={s.autocomplete}
			//  TODO: MOVE TO SEPARATE COMPONENT
			renderTags={(value, getTagProps) => {
				const numTags = value.length;
				const limitTags = 1;

				return (
					<>
						{value.slice(0, limitTags).map((option, index) => (
							<Chip {...getTagProps({ index })} key={index} label={option.title} />
						))}

						<Typography onClick={handleOpen}>{numTags > limitTags && ` +${numTags - limitTags}`}</Typography>

						<CustomModal
							PaperProps={{ className: s['modal-paper'] }}
							wrapperClassName={s.modal}
							className={s['modal-content']}
							title="Обрані сервіси"
							open={isOpen}
							onClose={handleClose}
						>
							{[...value]
								.sort((a, b) => a.title.length - b.title.length)
								.map((option, index) => (
									<Chip {...getTagProps({ index })} key={index} label={option.title} />
								))}
						</CustomModal>
					</>
				);
			}}
			options={options || []}
			noOptionsText="Не знайдено"
			renderInput={(params) => <TextField label="Сервіси" {...params} />}
			getOptionLabel={(option) => option.title}
			onInputChange={handleChangeAutocompleteInput}
			onChange={handleChangeAutocomplete}
			sx={{
				'& .MuiOutlinedInput-root': {
					padding: 0,
				},
			}}
			value={servicesValue}
		/>
	);
};
